import { useState } from 'react';
import { IconX, IconBrandGoogleDrive, IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { api, ApiError } from '../lib/api';
import { toast } from '../stores/toaster';

type Props = {
  dossierId: string;
  onImported?: () => void;
};

type DrivePiecePreview = {
  drive_file_id: string;
  drive_file_name: string;
  drive_mime_type: string;
  drive_size_bytes: number | null;
  drive_modified_time: string | null;
  subfolder: string;
  proposed_bucket: string;
  proposed_titre: string;
  classification_confidence: number | null;
  classification_rationale: string | null;
};

type SyncPreview = {
  dossier_id: string;
  ref_forlex: string | null;
  drive_folder_id: string;
  folder_match_mode: 'manual_override' | 'auto_ref_forlex';
  total_in_drive: number;
  already_imported: number;
  new_pieces: DrivePiecePreview[];
  last_sync_at: string | null;
};

type ImportResult = {
  drive_file_id: string;
  success: boolean;
  bucket: string | null;
  ad_id: string | null;
  error: string | null;
};

type ImportResponse = {
  imported: number;
  skipped: number;
  failed: number;
  results: ImportResult[];
};

type AsyncImportResponse = {
  task_id: string;
  async: true;
  selections_count: number;
  poll_url: string;
};

type TaskStatusResponse = {
  task_id: string;
  state: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'RETRY' | string;
  ready: boolean;
  result?: { status?: string; imported?: number; skipped?: number; failed?: number; error?: string };
  info?: string;
};

async function pollTask(taskId: string, onUpdate: (s: TaskStatusResponse) => void): Promise<TaskStatusResponse> {
  // Polling toutes les 2s, timeout 30 min.
  const start = Date.now();
  const maxMs = 30 * 60 * 1000;
  while (Date.now() - start < maxMs) {
    const s = await api.get<TaskStatusResponse>(`/tasks/${taskId}/status`);
    onUpdate(s);
    if (s.ready) return s;
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error('timeout polling task');
}

const fmtSize = (b: number | null) => {
  if (b == null) return '';
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} ko`;
  return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

const BUCKET_LABEL: Record<string, string> = {
  piece_client: 'Pièce client',
  piece_adverse: 'Pièce adverse',
  other_document: 'Autre doc.',
  pdf_file: 'PDF vrac',
  ignored: 'Ignorer',
};

export default function GDriveSyncButton({ dossierId, onImported }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<SyncPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCandidates, setErrorCandidates] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setErrorCandidates([]);
    setPreview(null);
    setSelected(new Set());
    try {
      const data = await api.get<SyncPreview>(`/dossiers/${dossierId}/gdrive/preview`);
      setPreview(data);
      // Par défaut on coche toutes les pièces qui ne sont pas "ignored"
      setSelected(
        new Set(
          data.new_pieces
            .filter((p) => p.proposed_bucket !== 'ignored')
            .map((p) => p.drive_file_id),
        ),
      );
    } catch (e) {
      const err = e as ApiError;
      const msg = err?.message || err?.problem?.detail || err?.problem?.title;
      if (err?.status === 503) {
        setError('Drive cabinet non configuré côté backend (M24_GDRIVE_KEY_PATH manquant).');
      } else if (err?.status === 404) {
        setError(
          msg ||
            'Dossier introuvable dans ForlexDocuments. Crée-le côté Forlex ou renseigne le folder_id manuellement.',
        );
      } else if (err?.status === 400) {
        setError(msg || 'Configuration manquante (ref_forlex ou gdrive_folder_id).');
      } else {
        setError(msg || 'Erreur réseau lors de la prévisualisation Drive.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const [asyncProgress, setAsyncProgress] = useState<string | null>(null);

  const runImport = async () => {
    if (!preview || selected.size === 0) return;
    setImporting(true);
    setAsyncProgress(null);
    try {
      const payload = {
        selections: preview.new_pieces
          .filter((p) => selected.has(p.drive_file_id))
          .map((p) => ({
            drive_file_id: p.drive_file_id,
            bucket: p.proposed_bucket === 'ignored' ? 'piece_client' : p.proposed_bucket,
            titre: p.proposed_titre,
          })),
      };
      // Backend bascule en async automatiquement si >= 5 pièces.
      // 202 → AsyncImportResponse ; 200 → ImportResponse sync.
      const resp = await api.post<ImportResponse | AsyncImportResponse>(
        `/dossiers/${dossierId}/gdrive/import`,
        payload,
      );
      if ('task_id' in resp && resp.async) {
        // Async — poll
        setAsyncProgress(`Import ${resp.selections_count} pièces en cours…`);
        const finalStatus = await pollTask(resp.task_id, (s) => {
          setAsyncProgress(
            s.state === 'STARTED'
              ? `Import en cours… (${resp.selections_count} pièces)`
              : s.state === 'PENDING'
                ? `En file d'attente… (${resp.selections_count} pièces)`
                : `État : ${s.state}`,
          );
        });
        if (finalStatus.state === 'SUCCESS' && finalStatus.result?.status === 'ok') {
          const r = finalStatus.result;
          toast.success(
            `Import Drive : ${r.imported ?? 0} réussi(s), ${r.failed ?? 0} échec(s), ${r.skipped ?? 0} ignoré(s).`,
          );
        } else {
          const errMsg = finalStatus.result?.error || finalStatus.info || finalStatus.state;
          toast.error('Import async KO : ' + errMsg);
        }
      } else {
        // Sync
        const r = resp as ImportResponse;
        toast.success(
          `Import Drive : ${r.imported} réussi(s), ${r.failed} échec(s), ${r.skipped} ignoré(s).`,
        );
      }
      if (onImported) onImported();
      setOpen(false);
    } catch (e) {
      const err = e as ApiError;
      toast.error('Import KO : ' + (err?.message || err?.problem?.detail || err?.problem?.title || 'inconnu'));
    } finally {
      setImporting(false);
      setAsyncProgress(null);
    }
  };

  return (
    <>
      <Btn variant="ghost" size="sm" onClick={() => void openModal()}>
        <IconBrandGoogleDrive size={14} stroke={1.6} className="mr-1" />
        Synchroniser Drive
      </Btn>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !importing && setOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-md bg-paper-elevated shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between border-b border-rule px-5 py-3">
              <div>
                <h2 className="font-serif text-[18px] font-medium text-text-primary">
                  Synchroniser depuis Google Drive
                </h2>
                <p className="mt-0.5 text-[12px] text-text-secondary">
                  Importe les nouvelles pièces présentes dans ForlexDocuments, sans toucher à ce qui
                  est déjà dans ADR.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !importing && setOpen(false)}
                className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
                aria-label="Fermer"
                disabled={importing}
              >
                <IconX size={18} stroke={1.6} />
              </button>
            </header>

            {loading ? (
              <div className="flex flex-1 items-center justify-center px-5 py-10 text-[13px] text-text-secondary">
                Inspection du dossier Drive en cours…
              </div>
            ) : error ? (
              <div className="px-5 py-6">
                <div className="flex items-start gap-2 rounded-md border border-cat-def/40 bg-cat-def-pastel p-3 text-[13px] text-cat-def">
                  <IconAlertTriangle size={18} stroke={1.6} className="mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p>{error}</p>
                    {errorCandidates.length > 0 && (
                      <p className="mt-2 text-[12px]">
                        Candidats proches : <strong>{errorCandidates.join(', ')}</strong>
                      </p>
                    )}
                    <p className="mt-2 text-[11px] text-text-secondary">
                      Règle cabinet : ADR ne crée jamais un folder client automatiquement.
                    </p>
                  </div>
                </div>
              </div>
            ) : preview ? (
              <>
                <div className="border-b border-rule bg-paper-card px-5 py-2 text-[12px] text-text-secondary">
                  <strong className="font-medium text-text-primary">{preview.new_pieces.length}</strong>{' '}
                  nouvelle{preview.new_pieces.length > 1 ? 's' : ''} pièce
                  {preview.new_pieces.length > 1 ? 's' : ''} dans Drive (sur {preview.total_in_drive}{' '}
                  au total, {preview.already_imported} déjà importées).{' '}
                  {preview.last_sync_at && (
                    <span>
                      Dernière sync : {new Date(preview.last_sync_at).toLocaleString('fr-BE')}.
                    </span>
                  )}
                  <span className="ml-1 text-text-tertiary">
                    [folder {preview.folder_match_mode === 'manual_override' ? 'manuel' : 'auto via ref_forlex'}]
                  </span>
                </div>

                {preview.new_pieces.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center px-5 py-10 text-[13px] text-text-secondary">
                    <IconCheck size={18} stroke={1.6} className="mr-2 text-cat-fav" />
                    Tout est synchronisé — aucune nouvelle pièce dans Drive.
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto px-5 py-3">
                    <table className="w-full text-[12px]">
                      <thead className="text-text-tertiary">
                        <tr className="border-b border-rule">
                          <th className="w-8 pb-2 text-left"></th>
                          <th className="pb-2 text-left">Fichier</th>
                          <th className="pb-2 text-left">Sous-farde</th>
                          <th className="pb-2 text-left">Classification</th>
                          <th className="pb-2 text-right">Taille</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.new_pieces.map((p) => {
                          const isSelected = selected.has(p.drive_file_id);
                          const isIgnored = p.proposed_bucket === 'ignored';
                          return (
                            <tr
                              key={p.drive_file_id}
                              className={`border-b border-rule/40 ${isIgnored ? 'opacity-50' : ''}`}
                            >
                              <td className="py-1.5">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelect(p.drive_file_id)}
                                  disabled={isIgnored}
                                />
                              </td>
                              <td className="py-1.5 text-text-primary" title={p.drive_file_name}>
                                <div className="max-w-md truncate">{p.drive_file_name}</div>
                              </td>
                              <td className="py-1.5 text-text-secondary">{p.subfolder}</td>
                              <td className="py-1.5">
                                <span className="rounded bg-paper-subtle px-1.5 py-0.5 text-[11px] text-text-secondary">
                                  {BUCKET_LABEL[p.proposed_bucket] || p.proposed_bucket}
                                </span>
                                {p.classification_confidence != null && (
                                  <span
                                    className="ml-2 text-[10px] text-text-tertiary"
                                    title={p.classification_rationale || ''}
                                  >
                                    conf. {(p.classification_confidence * 100).toFixed(0)}%
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 text-right text-text-tertiary">
                                {fmtSize(p.drive_size_bytes)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <p className="mt-3 text-[11px] text-text-tertiary">
                      MVP : toutes les pièces importées arrivent en <strong>Pièces client</strong>. Tu
                      pourras reclasser ensuite via l'outil migration. La classification IA ci-dessus
                      reste affichée pour info.
                    </p>
                  </div>
                )}

                <footer className="flex items-center justify-between border-t border-rule px-5 py-3">
                  <div className="text-[12px] text-text-secondary">
                    {asyncProgress ? (
                      <span className="text-accent-text">{asyncProgress}</span>
                    ) : (
                      <>
                        {selected.size} pièce{selected.size > 1 ? 's' : ''} sélectionnée
                        {selected.size > 1 ? 's' : ''}
                        {selected.size >= 5 && (
                          <span className="ml-2 text-[10px] text-text-tertiary">
                            (import async — peut prendre quelques minutes)
                          </span>
                        )}
                        .
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={() => setOpen(false)}
                      disabled={importing}
                    >
                      Annuler
                    </Btn>
                    <Btn
                      variant="primary"
                      size="sm"
                      onClick={() => void runImport()}
                      disabled={importing || selected.size === 0}
                    >
                      {importing ? 'Import en cours…' : `Importer ${selected.size}`}
                    </Btn>
                  </div>
                </footer>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
